import { useCallback, useMemo, useState } from 'react';

import { DemoDataPanel, type DemoDataMode, type DemoDataToggleItem } from '../../../../../components/ui/DemoDataPanel';
import { PageBreadcrumb, PageContentCard, PageTabs } from '../../../../../components/ui/ModulePage';
import { Button } from '../../../../../components/ui/Button';
import { Dialog } from '../../../../../components/ui/Dialog';
import { DropdownMenu } from '../../../../../components/ui/DropdownMenu';
import { Input } from '../../../../../components/ui/Input';
import { Link } from '../../../../../components/ui/Link';
import { Message, MessageStack, type MessageType } from '../../../../../components/ui/Message';
import { Select } from '../../../../../components/ui/Select';
import { Switch } from '../../../../../components/ui/Switch';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import { Tag } from '../../../../../components/ui/Tag';
import { deriveCouponStatus, formatIssueTime, formatUseTime, type Coupon, type CouponGroup, type CouponStatus } from '../fixtures';
import { type UnifiedRule } from '../stackRules';
import { addCoupon, consumePendingFocus, deleteCoupon as removeCoupon, deleteRule as dropRule, saveRule as persistRule, toggleCouponEnabled, useCouponStore } from '../../../../shared/couponStore';
import { StackRangeCell } from '../../../../shared/StackRangeCell';
import { CouponEditor } from './CouponEditor';
import styles from './CouponManagementPage.module.css';
import { RuleEditorDialog, type RuleEditorTarget, type RuleSaveInput } from './RuleEditorDialog';
import { RelationDetailDialog, RuleExampleDialog } from './RuleDialogs';
import { RulesView } from './RulesView';
import { couponStatusTone, couponTypeTagCategory } from './couponPresentation';

type View = 'coupons' | 'rules';

const listTabId = 'coupon-management-list';

const rulesTabId = 'coupon-management-rules';
const statusOptions = [
  { label: '不限', value: '' },
  { label: '进行中', value: '进行中' },
  { label: '未开启', value: '未开启' },
  { label: '已结束', value: '已结束' },
];

const newCouponDraft: Coupon = {
  id: 0,
  name: '新建优惠券',
  source: '优惠券',
  kind: 'ordinary',
  amount: '0.00',
  type: '满减券',
  threshold: '无门槛',
  issued: 0,
  sent: 0,
  used: 0,
  useTime: { type: 'relative', days: 30 },
  issueTime: { type: 'unlimited' },
  /* 新建的券未启用且没有发放窗口，派生状态为「未开启」；状态不再随数据一起存。 */
  enabled: false,
  stackMode: 'none',
  specified: [],
};

function statusDotClass(status: CouponStatus): string {
  const tone = couponStatusTone(status);
  return tone === 'active' ? styles.statusDotActive : tone === 'warning' ? styles.statusDotWarning : styles.statusDotMuted;
}

type EditorExit = { kind: 'back' } | { kind: 'rules'; focusId: number } | { kind: 'tab'; view: View };
type Notice = { message: string; type: MessageType };

export function CouponManagementPage() {
  const [view, setView] = useState<View>('coupons');
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);
  /**
   * 券数据是跨模块共享的（电子推荐卡的奖励券也在这份里），键是 singleton store，
   * 不再放在本页 state 中。跨页跳转带来的「去设置 / 查看全部」诉求同样从 store 读。
   */
  const { coupons, groups, pendingFocus, recommendationCoupons } = useCouponStore();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [relationId, setRelationId] = useState<number | null>(null);
  const [exampleOpen, setExampleOpen] = useState(false);
  const [ruleEditor, setRuleEditor] = useState<RuleEditorTarget | null>(null);
  const [editorDirty, setEditorDirty] = useState(false);
  const [pendingEditorExit, setPendingEditorExit] = useState<EditorExit | null>(null);
  const [pendingCouponDelete, setPendingCouponDelete] = useState<Coupon | null>(null);
  /* 演示数据按 tab 当一个页面：各自一份开关状态，配置气泡里只显示当前 tab 的那一行。
   * label 只进可访问名称（界面统一显示「模拟数据」），便于测试与读屏区分两个 tab 的开关。 */
  const [couponsDataMode, setCouponsDataMode] = useState<DemoDataMode>('demo');
  const [rulesDataMode, setRulesDataMode] = useState<DemoDataMode>('demo');
  /* 跨页跳转进来的诉求都发生在规则页，展示期间把 tab 也顶到「叠加规则设置」，别让弹窗浮在优惠券列表上。 */
  const effectiveView: View = pendingFocus ? 'rules' : view;
  const demoDataItems: readonly DemoDataToggleItem[] = effectiveView === 'coupons'
    ? [{ key: 'coupons', label: '优惠券管理', mode: couponsDataMode, onModeChange: setCouponsDataMode }]
    : [{ key: 'rules', label: '叠加规则设置', mode: rulesDataMode, onModeChange: setRulesDataMode }];

  const entities = useMemo(() => [...coupons, ...recommendationCoupons], [coupons, recommendationCoupons]);
  const editingCoupon = creatingCoupon ? newCouponDraft : editingCouponId != null ? coupons.find((coupon) => coupon.id === editingCouponId) : undefined;
  /*
   * 推荐卡里点「去设置 / 查看全部」会带着 couponId 跨页跳转过来，这里在渲染期就直接算出
   * 该开哪个弹窗（不做 effect 中转，省一次挂载后 setState）；关闭或保存时把诉求消费掉。
   */
  const focusTarget = pendingFocus && entities.find((coupon) => coupon.id === pendingFocus.couponId);
  const activeRuleEditor = pendingFocus?.intent === 'rule' && focusTarget ? ruleEditorTargetFor(focusTarget.id) : ruleEditor;
  const activeRelationId = pendingFocus?.intent === 'relation' ? pendingFocus.couponId : relationId;
  const activeRelationCoupon = activeRelationId != null ? entities.find((coupon) => coupon.id === activeRelationId) : undefined;
  const breadcrumbCurrent = editingCoupon ? (creatingCoupon ? '新增优惠券' : '编辑优惠券') : effectiveView === 'coupons' ? '优惠券管理' : '叠加规则设置';

  function showNotice(message: string, type: MessageType = 'primary') {
    setNotice({ message, type });
  }

  function toggleCoupon(id: number) {
    const coupon = entities.find((item) => item.id === id);
    toggleCouponEnabled(id);
    showNotice(coupon?.enabled ? '已停用；已发放优惠券仍可继续使用' : '已启用，可继续发放优惠券');
  }

  function ruleEditorTargetFor(id: number): RuleEditorTarget {
    const coupon = entities.find((item) => item.id === id);
    return coupon && coupon.stackMode !== 'none' ? { key: `single-${id}` } : { focusId: id };
  }

  function openRulesFor(id: number) {
    setView('rules');
    setCreatingCoupon(false);
    setEditingCouponId(null);
    setRelationId(null);
    setRuleEditor(ruleEditorTargetFor(id));
  }

  const onEditorDirtyChange = useCallback((dirty: boolean) => setEditorDirty(dirty), []);

  function createCoupon(): Coupon {
    const nextId = coupons.reduce((maximum, coupon) => Math.max(maximum, coupon.id), 0) + 1;
    const coupon = { ...newCouponDraft, id: nextId };
    addCoupon(coupon);
    setCreatingCoupon(false);
    return coupon;
  }

  function finishEditorExit(exit: EditorExit, save = false) {
    const createdCoupon = save && creatingCoupon ? createCoupon() : undefined;
    if (save) showNotice(createdCoupon ? '优惠券创建成功' : '优惠券保存成功');
    setEditorDirty(false);
    setPendingEditorExit(null);
    setCreatingCoupon(false);
    if (exit.kind === 'tab') {
      setEditingCouponId(null);
      setView(exit.view);
      return;
    }
    if (exit.kind === 'rules') {
      setEditingCouponId(null);
      setRelationId(null);
      setView('rules');
      setRuleEditor(ruleEditorTargetFor(createdCoupon?.id ?? exit.focusId));
      return;
    }
    setEditingCouponId(null);
  }

  function requestEditorExit(exit: EditorExit) {
    if (editingCoupon && editorDirty) {
      setPendingEditorExit(exit);
      return;
    }
    finishEditorExit(exit);
  }

  function saveRule(input: RuleSaveInput) {
    persistRule(input);
    showNotice('叠加规则已保存');
    setRuleEditor(null);
    setRelationId(null);
    consumePendingFocus();
  }

  function deleteRule(rule: UnifiedRule) {
    dropRule(rule);
    showNotice('叠加规则已删除');
  }

  function issueCoupon(coupon: Coupon) {
    showNotice(`已发起“${coupon.name}”发券`);
  }

  function promoteCoupon(coupon: Coupon) {
    showNotice(`已发起“${coupon.name}”推广`);
  }

  function deleteCoupon(coupon: Coupon) {
    removeCoupon(coupon.id);
    setPendingCouponDelete(null);
    showNotice('优惠券已删除');
  }

  return <section className={styles.kit}>
    <PageBreadcrumb current={breadcrumbCurrent} items={['招生营销', '优惠券管理']} />
    <PageContentCard className={styles.contentCard}>
      <PageTabs
        ariaLabel="优惠券管理页签"
        onValueChange={(value) => requestEditorExit({ kind: 'tab', view: value === rulesTabId ? 'rules' : 'coupons' })}
        panelClassName={styles.panel}
        tabs={[
          { id: listTabId, label: '优惠券管理', panel: editingCoupon ? <CouponEditor coupon={editingCoupon} entities={entities} groups={groups} isNew={creatingCoupon} onBack={() => requestEditorExit({ kind: 'back' })} onDirtyChange={onEditorDirtyChange} onOpenRelation={setRelationId} onOpenRules={(id) => requestEditorExit({ kind: 'rules', focusId: id })} onSave={(next) => finishEditorExit(next === 'rules' ? { kind: 'rules', focusId: editingCoupon.id } : { kind: 'back' }, true)} /> : <CouponList coupons={couponsDataMode === 'initial' ? [] : coupons} entities={entities} groups={groups} key={couponsDataMode} onAdd={() => { setCreatingCoupon(true); setEditingCouponId(null); }} onEdit={(id) => { setCreatingCoupon(false); setEditingCouponId(id); }} onIssue={issueCoupon} onOpenRelation={setRelationId} onOpenRules={openRulesFor} onPromote={promoteCoupon} onRequestDelete={setPendingCouponDelete} onToggle={toggleCoupon} /> },
          { id: rulesTabId, label: '叠加规则设置', panel: <RulesView entities={entities} groups={groups} initialMode={rulesDataMode === 'initial'} key={rulesDataMode} onAddRule={() => setRuleEditor({})} onDeleteRule={deleteRule} onEditRule={(key) => setRuleEditor({ key })} onOpenExample={() => setExampleOpen(true)} onShowNotice={showNotice} /> },
          { id: 'coupon-management-details', label: '优惠券发放明细', disabled: true },
        ]}
        value={effectiveView === 'rules' ? rulesTabId : listTabId}
      />
    </PageContentCard>

    <MessageStack>{notice ? <Message message={notice.message} onOpenChange={(open) => { if (!open) setNotice(null); }} open type={notice.type} /> : null}</MessageStack>

    <DemoDataPanel items={demoDataItems} />

    {activeRuleEditor ? <RuleEditorDialog editing={activeRuleEditor} entities={entities} groups={groups} key={`${activeRuleEditor.key ?? 'new'}-${activeRuleEditor.focusId ?? ''}`} onNotice={showNotice} onOpenChange={(open) => { if (!open) { setRuleEditor(null); if (pendingFocus) setView('rules'); consumePendingFocus(); } }} onSave={saveRule} /> : null}
    <RuleExampleDialog onOpenChange={setExampleOpen} open={exampleOpen} />
    <RelationDetailDialog coupon={activeRelationCoupon} entities={entities} groups={groups} key={activeRelationCoupon?.id ?? 'none'} onEdit={openRulesFor} onOpenChange={(open) => { if (!open) { setRelationId(null); if (pendingFocus) setView('rules'); consumePendingFocus(); } }} open={activeRelationCoupon !== undefined} />
    {pendingCouponDelete ? <Dialog
      footer={<><Button onClick={() => setPendingCouponDelete(null)} type="default">取消</Button><Button onClick={() => deleteCoupon(pendingCouponDelete)} type="danger">删除</Button></>}
      onOpenChange={(open) => { if (!open) setPendingCouponDelete(null); }}
      open
      size="sm"
      title="删除优惠券"
    ><p className={styles.confirmText}>确定删除“{pendingCouponDelete.name}”吗？删除后，该券相关叠加关系将一并清除。</p></Dialog> : null}
    {pendingEditorExit ? <Dialog
      className={styles.compactConfirm}
      footer={<><Button onClick={() => setPendingEditorExit(null)} type="default">继续编辑</Button><Button onClick={() => finishEditorExit(pendingEditorExit)}>放弃修改</Button></>}
      onOpenChange={(open) => { if (!open) setPendingEditorExit(null); }}
      open
      size="sm"
      title="放弃未保存的修改？"
    ><p className={styles.confirmText}>当前优惠券修改尚未保存，关闭后将丢失。</p></Dialog> : null}
  </section>;
}

interface CouponListProps {
  coupons: readonly Coupon[];
  entities: readonly Coupon[];
  groups: readonly CouponGroup[];
  onAdd: () => void;
  onEdit: (id: number) => void;
  onIssue: (coupon: Coupon) => void;
  onOpenRelation: (id: number) => void;
  onOpenRules: (id: number) => void;
  onPromote: (coupon: Coupon) => void;
  onRequestDelete: (coupon: Coupon) => void;
  onToggle: (id: number) => void;
}

function CouponList({ coupons, entities, groups, onAdd, onEdit, onIssue, onOpenRelation, onOpenRules, onPromote, onRequestDelete, onToggle }: CouponListProps) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const filtered = coupons.filter((coupon) => coupon.name.includes(query.trim()) && (!status || deriveCouponStatus(coupon) === status));

  const columns: TableColumn<Coupon>[] = [
    { fixed: 'left', key: 'name', render: (coupon) => <Link onClick={() => onEdit(coupon.id)}>{coupon.name}</Link>, title: '名称', width: 160 },
    { align: 'right', fixed: 'left', key: 'amount', title: '面额', width: 100 },
    { key: 'type', render: (coupon) => <Tag category={couponTypeTagCategory(coupon.type)}>{coupon.type}</Tag>, title: '类型', width: 110 },
    { key: 'threshold', title: '使用门槛', width: 120 },
    { className: styles.numeric, key: 'issued', render: (coupon) => `${coupon.issued} / ${coupon.sent} / ${coupon.used}`, title: '发行/已发放/已使用', width: 180 },
    { className: styles.numeric, key: 'useTime', render: (coupon) => formatUseTime(coupon.useTime), title: '用券时间', width: 190 },
    { className: styles.numeric, key: 'issueTime', render: (coupon) => formatIssueTime(coupon.issueTime), title: '发券时间', width: 150 },
    {
      key: 'relation',
      render: (coupon) => <StackRangeCell coupon={coupon} entities={entities} groups={groups} onOpen={onOpenRelation} />,
      title: '叠加范围',
      width: 190,
    },
    { key: 'status', render: (coupon) => { const current = deriveCouponStatus(coupon); return <span className={styles.statusText}><span aria-hidden="true" className={statusDotClass(current)} />{current}</span>; }, title: '状态', width: 100 },
    { key: 'enabled', render: (coupon) => <Switch checked={coupon.enabled} label={`${coupon.name}启用状态`} onCheckedChange={() => onToggle(coupon.id)} size="small" />, title: '启用/停用', width: 100 },
    {
      fixed: 'right',
      key: 'actions',
      render: (coupon) => <span className={styles.couponActions}>
        <Link onClick={() => onEdit(coupon.id)}>编辑</Link>
        <Link onClick={() => onIssue(coupon)}>发券</Link>
        <DropdownMenu
          ariaLabel={`${coupon.name}更多操作`}
          items={[
            { id: 'stack', label: '设置叠加范围' },
            { id: 'promote', label: '推广' },
            { id: 'delete', label: '删除', separatorBefore: true },
          ]}
          onItemSelect={(item) => {
            if (item.id === 'stack') onOpenRules(coupon.id);
            else if (item.id === 'promote') onPromote(coupon);
            else onRequestDelete(coupon);
          }}
          trigger={<button className={styles.moreAction} type="button"><span aria-hidden="true">···</span></button>}
        />
      </span>,
      title: '操作',
      width: 172,
    },
  ];

  return <>
    <div className={styles.filters}>
      <label>名称<Input ariaLabel="优惠券名称" className={styles.filterInput} onValueChange={(value) => { setQuery(value); setPage(1); }} placeholder="请输入优惠券名称" value={query} /></label>
      <label>状态<Select ariaLabel="优惠券状态" className={styles.filterSelect} onValueChange={(value) => { setStatus(value); setPage(1); }} options={statusOptions} value={status} /></label>
      <div className={styles.filterActions}><Button>查询</Button><Button onClick={() => { setQuery(''); setStatus(''); setPage(1); }} type="default">重置</Button></div>
    </div>
    <div className={styles.toolbar}>
      <Button aria-label="新增优惠券" onClick={onAdd}>新增</Button>
    </div>
    <Table ariaLabel="优惠券列表" columns={columns} emptyText="暂无优惠券" pagination={{ ariaLabel: '优惠券列表分页', footerStart: <span>共 {filtered.length} 条优惠券</span>, onPageChange: setPage, page, pageSize: 10 }} rowKey={(coupon) => coupon.id} rows={filtered} />
  </>;
}
